import type { ReactNode } from "react";
import { Loader2 } from "lucide-react";
import { FileTypeIcon } from "@/components/ui/documents/file-type-icon";
import type { FolderPathSegment } from "@/domains/documents/utils/folderPathResolver";
import { DocumentBreadcrumbHover } from "./DocumentBreadcrumbHover";

interface LinkedDocumentRowProps {
  filename: string;
  buildingId: number;
  /** The file's folder path, shown on hover; none for a file with no known path. */
  breadcrumbSegments?: FolderPathSegment[] | null;
  /** Opens the file; absent when it cannot be previewed. */
  onOpen?: () => void;
  /** Tooltip on a file that cannot be opened. */
  unavailableLabel?: string;
  /** The inline spinner's label, while the file is still being processed. */
  processingLabel?: string | null;
  /** Trailing slot, revealed on hover — the issue panel's remove button. */
  action?: ReactNode;
}

/**
 * One attached file, as the issue panel lists them: a row with the
 * file-type icon and the name, which opens the file, and a border only on
 * hover. Shared by issue
 * attachments and brief appendices so an attachment looks the same wherever
 * it is listed.
 */
export function LinkedDocumentRow({
  filename,
  buildingId,
  breadcrumbSegments,
  onOpen,
  unavailableLabel,
  processingLabel,
  action,
}: LinkedDocumentRowProps) {
  return (
    <li className="group flex items-center gap-2 rounded-md border border-transparent px-2 py-1.5 hover:border-foreground/40">
      <FileTypeIcon filename={filename} className="h-6 w-6" />
      {/* WHA-3457: hover the filename for the interactive folder path. */}
      <DocumentBreadcrumbHover segments={breadcrumbSegments} buildingId={buildingId}>
        {onOpen ? (
          <button
            type="button"
            onClick={onOpen}
            className="min-w-0 flex-1 truncate text-left text-xs hover:underline focus:underline focus:outline-none"
            title={filename}
          >
            {filename}
          </button>
        ) : (
          <span
            className="min-w-0 flex-1 truncate text-xs text-muted-foreground"
            title={unavailableLabel}
          >
            {filename}
          </span>
        )}
      </DocumentBreadcrumbHover>
      {/* Inline processing chip — same spinner as the upload modal, anchored to
          the row instead of floating in the panel. Plain spinner + text — no
          tinted pill behind it. */}
      {processingLabel && (
        <span className="inline-flex shrink-0 items-center gap-1 text-[10px] font-medium text-muted-foreground">
          <Loader2 className="h-3 w-3 animate-spin" />
          {processingLabel}
        </span>
      )}
      {action}
    </li>
  );
}
